import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { config } from '@snapshots/config';
import { Captures, Family, People, Session, type Identity } from '@snapshots/domain';
import { usePreferences } from '@beyond-js/ui/react';
import { Catalog, en, es, type Language } from '@snapshots/locale';
import { Notices } from '@snapshots/notifications';
import { device } from '@snapshots/theme';
import { Client, Refusal } from '@snapshots/transport';
import { Entry } from './entry';
import { Unsaved } from './unsaved';

/** How far the application has got in working out who is here; `departing` is leaving for Accounts */
export type Standing = 'reading' | 'in' | 'out' | 'unavailable' | 'departing';

/**
 * Why the person arrived signed out and the hand-off did not start by itself (D25, D26): the session the
 * browser still carried expired (`expired`) or was ended at Accounts (`revoked`) within the loop guard,
 * the account is suspended and signing in again would not help (`suspended`), they signed out of
 * Snapshots and Accounts confirmed it or did not (`signedout`, `unconfirmed`), they signed out of Beyond
 * (`beyond`, Q09), or a departure to Accounts within the last minute came back without a session
 * (`incomplete`, the loop guard). A session that ends while the person works is `Session`'s, never one
 * of these
 */
export type Ending = 'expired' | 'revoked' | 'suspended' | 'signedout' | 'beyond' | 'unconfirmed' | 'incomplete';

interface Value {
	standing: Standing;
	identity?: Identity;
	refusal?: Refusal;
	/** Why the person is signed out, when it happened here */
	ending?: Ending;
	/** A catalog key for what the signed-in person must know now: a sign-out that was not confirmed */
	warning?: string;
	session: Session;
	/** The transport, whose `guard` the signed-in frame gives to `Session` */
	client: Client;
	/** Takes the identity a renewal read, without changing who is here or what is shown */
	adopt: (identity: Identity) => void;
	/** The departures to Accounts of this tab: the sign-in screen marks one when the person asks */
	entry: Entry;
	captures: Captures;
	/** The names of the people pages mention, from Accounts' directory through this service */
	people: People;
	/** The family bar's descriptor, relayed through this service */
	family: Family;
	/** The person's suite inbox, relayed through this service */
	notices: Notices;
	/** The language in effect: the account's on arrival, then whatever is chosen here on this device */
	language: Language;
	/** Reads who is here again; resolves whether the service answered (an outage is `false`) */
	refresh: () => Promise<boolean>;
	/**
	 * Signing out (Q09), as the family bar's `account.signout` runs it: `end` ends this product's
	 * session, marking the departure first when the bar goes on to Accounts' `/leave` (`beyond`), and
	 * `after` says how signing out of Snapshots alone went when there is no `/leave` to go to
	 */
	end: (beyond: boolean) => Promise<void>;
	after: () => Promise<void>;
	/** What screens hold unsaved, so signing out warns before losing it */
	unsaved: Unsaved;
	text: (key: string, values?: Record<string, string | number>) => string;
	/** A count in words, by the language's plural rules (`<key>.one`, `<key>.other`) */
	count: (key: string, count: number, values?: Record<string, string | number>) => string;
	date: (value: string | Date | undefined) => string;
	/** How far a moment is from now: "3 minutes ago" */
	relative: (value: string | Date | undefined) => string;
	/** A moment of the day: "15:53" */
	time: (value: string | Date | undefined) => string;
	/** A moment without its year: "Oct 17, 3:53 PM" */
	short: (value: string | Date | undefined) => string;
	size: (bytes: number | undefined) => string;
}

const Context = createContext<Value | undefined>(undefined);

/**
 * Who is here, in what language, and looking how. "Still reading" is not "signed out", and an
 * unreachable service is neither: the four standings are kept apart so no screen tells a person
 * something untrue about their session. The language and appearance Beyond Accounts keeps for the
 * person, which this service passes on, win once when the person arrives.
 */
export function Presence({ children }: { children: ReactNode }) {
	const client = useMemo(() => new Client(config.api), []);
	const session = useMemo(() => new Session(client), [client]);
	const captures = useMemo(() => new Captures(client), [client]);
	const people = useMemo(() => new People(client), [client]);
	const family = useMemo(() => new Family(client), [client]);
	const preferences = device.preferences;
	const entry = useMemo(() => new Entry(), []);
	const unsaved = useMemo(() => new Unsaved(), []);
	// How the last sign-out went at the service, for `after`
	const outcome = useRef<'signedout' | 'unconfirmed' | 'failed'>('failed');
	// Only an arrival leaves for Accounts by itself: a sign-out shows the sign-in screen saying why, and a
	// session that ends while the person works is `Session`'s (the signed-in frame's)
	const arrival = useRef(true);
	// Once leaving, a read still answering (React runs the first effect twice in development) changes nothing
	const leaving = useRef(false);
	// Whose values were applied: the account's preferences win on arrival, not on every read after it
	const arrived = useRef<string>();

	const [standing, setStanding] = useState<Standing>('reading');
	const [identity, setIdentity] = useState<Identity>();
	const [refusal, setRefusal] = useState<Refusal>();
	const [ending, setEnding] = useState<Ending>();
	const [warning, setWarning] = useState<string>();
	const current = usePreferences(preferences);
	// A change made in the family's "Language and appearance" dialog applies here at once (D54)
	const language = current.locale as Language;
	const notices = useMemo(() => new Notices(client, () => language), [client, language]);

	const refresh = useCallback(async (): Promise<boolean> => {
		try {
			const found = await session.identity();
			if (found.preferences && arrived.current !== found.account) preferences.apply(found.preferences);
			arrived.current = found.account;
			setIdentity(found);
			arrival.current = false;
			entry.clear();
			setRefusal(undefined);
			setEnding(undefined);
			setStanding('in');
			return true;
		} catch (cause) {
			const problem = cause instanceof Refusal ? cause : new Refusal('UNREACHABLE', String(cause));
			if (leaving.current) return true;
			arrived.current = undefined;
			setIdentity(undefined);
			setRefusal(problem);
			// An outage is not an arrival answered: its retry may still leave for Accounts by itself
			if (problem.unreachable) {
				setStanding('unavailable');
				return false;
			}
			// The first read to be answered is the arrival, whichever of two concurrent reads it is
			const arriving = arrival.current;
			arrival.current = false;
			const { pathname, search } = globalThis.location;
			const ended = problem.ended;
			// D26: signing in again would not help a suspended account, so nothing leaves for Accounts
			if (ended === 'suspended') {
				setEnding('suspended');
				setStanding('out');
				return true;
			}
			// D25: an arrival leaves at once, unless a return failed, the person signed out here or the guard holds
			if (arriving && !Session.failure(pathname, search) && entry.automatic && entry.depart()) {
				leaving.current = true;
				setStanding('departing');
				globalThis.location.assign(session.signin(Session.destination(pathname, search)));
				return true;
			}
			if (arriving && entry.left) setEnding(entry.beyond ? 'beyond' : 'signedout');
			else if (ended) setEnding(ended);
			else if (arriving && entry.recent && !Session.failure(pathname, search)) setEnding('incomplete');
			setStanding('out');
			return true;
		}
	}, [session, entry, preferences]);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	const catalog = useMemo(() => new Catalog(language, language === 'es' ? es : en, en), [language]);
	// Stable for one language, so a change of appearance does not rebuild what is keyed on the words
	const words = useMemo<Pick<Value, 'text' | 'count' | 'date' | 'relative' | 'time' | 'short' | 'size'>>(
		() => ({
			text: (key, values) => catalog.text(key, values),
			count: (key, count, values) => catalog.count(key, count, values),
			date: value => catalog.date(value),
			relative: value => catalog.relative(value),
			time: value => catalog.time(value),
			short: value => catalog.short(value),
			size: bytes => catalog.size(bytes)
		}),
		[catalog]
	);
	const value = useMemo<Value>(
		() => ({
			standing, identity, refusal, ending, warning, session, client, entry, captures, people, family, notices, language, refresh,
			adopt: found => setIdentity(found),
			// Leaving for Accounts' `/leave`, the departure is marked first, so coming back signed out never
			// leaves again by itself, even when the bar stopped waiting for this service's answer
			end: async beyond => {
				setWarning(undefined);
				if (beyond) {
					entry.leave(true);
					leaving.current = true;
				}
				outcome.current = await session.leave().then(
					ended => (ended ? 'signedout' : 'unconfirmed'),
					() => 'failed'
				);
			},
			// Signing out of Snapshots alone is said as it happened: confirmed by Accounts, done here only, or not done at all
			after: async () => {
				if (outcome.current === 'failed') setWarning('signout.failed');
				else {
					setEnding(outcome.current);
					entry.leave();
				}
				await refresh();
			},
			unsaved,
			...words
		}),
		[standing, identity, refusal, ending, warning, session, client, entry, unsaved, captures, people, family, notices, language, refresh, words]
	);
	return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePresence(): Value {
	const value = useContext(Context);
	if (!value) throw new Error('usePresence needs a Presence above it');
	return value;
}
